import dayjs from "dayjs";
import Utils from "../Utils";
import services from "../services";
import NestMoney from "../NestMoney";

const strict = true;

const getDefaultState = () => {
  return {
    toSearch: "",
    toFilter: "DONE",
    sortConfig: {
      list: ["A-Z", "Z-A"],
      order: "A-Z"
    },
    dateConfig: {
      range: {
        dateFrom: null,
        dateTo: null
      },
      dateToUseConfig: {
        active: "SCHEDULE"
      }
    },
    filterCriteriaResults: "",
    billAppointments: [],
    totalBillAppointments: 0,
    pageDescTitle: "",
    pageDescText: "",
    pageDescParams: {
      billProtocolCode: "-"
    }
  };
};

const state = getDefaultState();

const getters = {
  items: state => state.billAppointments,
  toSearch: state => state.toSearch,
  orderList: state => state.sortConfig.list,
  order: state => state.sortConfig.order,
  dateRange: state => state.dateConfig.range,
  totalItems: state => state.totalBillAppointments,
  filterCriteriaResults: state => state.filterCriteriaResults,
  toFilter: state => state.toFilter,
  pageDescTitle: state => state.pageDescTitle,
  pageDescText: state => state.pageDescText,
  pageDescParams: state => state.pageDescParams
};

const mutations = {
  RESET(state) {
    // acquire initial state
    let s = getDefaultState();
    Object.keys(s).forEach(key => {
      state[key] = s[key];
    });
  },
  SET_TO_FILTER(state, toFilter) {
    state.toFilter = toFilter;
  },
  SET_TO_SEARCH(state, toSearch) {
    state.toSearch = toSearch;
  },
  SET_ORDER(state, order) {
    state.sortConfig.order = order;
  },
  updateDateRange(state, dateRange) {
    state.dateConfig.range = dateRange;
  },
  updateDateToUse(state, config) {
    state.dateConfig.dateToUseConfig = config;
  },
  SET_FILTER_CRITERIA_RESULTS(state) {
    let criteria = [];
    if (state.toSearch) {
      criteria.push("'" + state.toSearch + "'");
    }
    if (state.dateConfig.range.dateFrom && state.dateConfig.range.dateTo) {
      let DATE_FORMAT = "DD/MM/YYYY";
      criteria.push(
        "'" +
          dayjs(state.dateConfig.range.dateFrom).format(DATE_FORMAT) +
          " - " +
          dayjs(state.dateConfig.range.dateTo).format(DATE_FORMAT) +
          "'"
      );
    }
    state.filterCriteriaResults = criteria.join(" & ");
  },
  SET_BILL_APPOINTMENTS(state, billAppointments) {
    for (let appointment of billAppointments) {
      if (appointment.state == "ACCEPTED" && !appointment.acceptedToWork) {
        appointment.state = "ACCEPTED_POST";
      }
    }
    state.billAppointments = billAppointments;
  },
  SET_TOTAL_BILL_APPOINTMENTS(state, totalBillAppointments) {
    state.totalBillAppointments = totalBillAppointments;
  },
  SET_PAGE_TITLE(state, pageDescTitle) {
    state.pageDescTitle = pageDescTitle;
  },
  SET_PAGE_TEXT(state, pageDescText) {
    state.pageDescText = pageDescText;
  },
  SET_PAGE_DESC_PARAMS(state, params) {
    state.pageDescParams = params;
  }
};

const actions = {
  async getItems({ commit, state, getters }, officeId) {
    try {
      let parameters = buildParamsObject(officeId, state);
      let response =
        await services.appointmentsService.fetchAppointmentsForBill(parameters);
      let appointmentList = [];
      let numberOfAppointments = response.numberOfAppointments;
      if (numberOfAppointments > 0) {
        appointmentList = filterBillableAppointments(
          response.appointmentList,
          state.toFilter
        );
        appointmentList = sortAppointmentList(
          appointmentList,
          state.sortConfig.order
        );
        numberOfAppointments = appointmentList.length;
      }

      commit("SET_BILL_APPOINTMENTS", appointmentList);
      commit("SET_TOTAL_BILL_APPOINTMENTS", numberOfAppointments);
    } catch (error) {
      let errorDialogDetails = {
        ...error
      };
      commit("SET_BILL_APPOINTMENTS", []);
      commit("SET_TOTAL_BILL_APPOINTMENTS", 0);
      if (error.type == Utils.errorTypes.STATUS) {
        errorDialogDetails.dataName = "hfManagement";
      }

      commit("error/SET_ERROR", errorDialogDetails, {
        root: true
      });
    } finally {
      commit("SET_FILTER_CRITERIA_RESULTS");
    }
  },
  async addDoneAppointment({ commit, rootGetters }, appointmentId) {
    try {
      let LOADING_DIALOG_TEXT = "dialogs.loading.contentLoadingDialogMsg";
      commit("loading/OPEN_LOADING_DIALOG", LOADING_DIALOG_TEXT, {
        root: true
      });
      let OFFICE_ID = rootGetters["hfManagement/officeId"];
      let BILL_ID = rootGetters["bills/activeBill"].id;
      await services.billsService.addDoneAppointment(
        BILL_ID,
        OFFICE_ID,
        appointmentId
      );
      commit("loading/CLOSE_LOADING_DIALOG", null, {
        root: true
      });
    } catch (error) {
      commit("loading/CLOSE_LOADING_DIALOG", null, {
        root: true
      });
      let errorDialogDetails = {
        ...error
      };
      if (error.type == Utils.errorTypes.STATUS) {
        errorDialogDetails.dataName = "bills";
      }
      commit("error/SET_ERROR", errorDialogDetails, {
        root: true
      });
    }
  },
  async callAddAcceptedAppointment({ commit, rootGetters }, app) {
    try {
      let LOADING_DIALOG_TEXT = "dialogs.loading.contentLoadingDialogMsg";
      commit("loading/OPEN_LOADING_DIALOG", LOADING_DIALOG_TEXT, {
        root: true
      });
      let officeId = rootGetters["hfManagement/officeId"];
      let appointmentId = app.appointmentId;
      let invoiceNumber = app.invoiceNumber;
      let invoiceDate = app.invoiceDate;
      let invoiceTotal = app.invoiceTotal;
      let payload = {
        officeId: officeId,
        billId: rootGetters["bills/activeBill"].id,
        appointmentId: appointmentId,
        deliveryDate: app.appointmentDeliveryDate
      };
      await services.billsService.addAcceptedAppointment(payload);
      if (invoiceNumber || invoiceDate || invoiceTotal) {
        // fondoest/issues#981: if the value contains only letters, is not allowed
        let invNumber = invoiceNumber;
        if (/^[a-zA-Z]+$/.test(invNumber)) {
          invNumber = "";
        }
        await services.billsService.callSaveAppointmentInvoiceInfo(
          officeId,
          appointmentId,
          invNumber,
          invoiceDate,
          invoiceTotal
        );
      }
      commit("loading/CLOSE_LOADING_DIALOG", null, {
        root: true
      });
    } catch (error) {
      commit("loading/CLOSE_LOADING_DIALOG", null, {
        root: true
      });
      let errorDialogDetails = {
        ...error
      };
      if (error.type == Utils.errorTypes.STATUS) {
        errorDialogDetails.dataName = "bills";
      }
      commit("error/SET_ERROR", errorDialogDetails, {
        root: true
      });
    }
  }
};

function buildParamsObject(officeId, state) {
  let DATE_FORMAT = "DD/MM/YYYY";
  let parameters = {
    officeId
  };
  parameters["size"] = 1000;
  parameters["state"] = state.toFilter;
  if (state.dateConfig?.range && state.dateConfig.range.dateFrom)
    parameters["dateFrom"] = dayjs(state.dateConfig.range.dateFrom).format(
      DATE_FORMAT
    );
  if (state.dateConfig?.range && state.dateConfig.range.dateTo)
    parameters["dateTo"] = dayjs(state.dateConfig.range.dateTo).format(
      DATE_FORMAT
    );
  if (
    state.dateConfig?.dateToUseConfig &&
    state.dateConfig.dateToUseConfig.active
  )
    parameters["dateToUse"] = state.dateConfig.dateToUseConfig.active;
  if (state.toSearch) parameters["toSearch"] = state.toSearch;
  return parameters;
}

function filterBillableAppointments(appointmentList, toFilter) {
  return toFilter == "DONE"
    ? appointmentList
    : appointmentList.filter(a => {
        let exemption = Number.parseFloat(
          NestMoney.formatCurrencyForOP(a.exemption)
        );
        let price = Number.parseFloat(NestMoney.formatCurrencyForOP(a.price));

        return price > exemption;
      });
}

function sortAppointmentList(appointmentList, sortBy) {
  return appointmentList.sort((appointmentA, appointmentB) => {
    if (!appointmentA.serviceName || !appointmentB.serviceName) return 0;
    let serviceNameA = appointmentA.serviceName.toUpperCase();
    let serviceNameB = appointmentB.serviceName.toUpperCase();
    if (serviceNameA < serviceNameB) {
      return sortBy === "A-Z" ? -1 : 1;
    }
    if (serviceNameA > serviceNameB) {
      return sortBy === "A-Z" ? 1 : -1;
    }
    return 0;
  });
}

export default {
  namespaced: true,
  strict,
  state,
  getters,
  mutations,
  actions
};
